/**
 * Maps a Gemini JSON metadata response into the raw shape consumed by
 * `normalizeAnalysisResult` (same contract as `mockMetadata.js`).
 */

import { GEMINI_MODEL } from "./prompt.js";

const passThrough = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  if (!("value" in entry)) return null;
  return {
    value: entry.value ?? null,
    confidence: typeof entry.confidence === "number" ? entry.confidence : 0,
  };
};

const primaryGenre = (genresEntry) => {
  const base = passThrough(genresEntry);
  if (!base) return null;
  const list = Array.isArray(base.value) ? base.value.filter(Boolean) : [];
  if (list.length === 0) return { value: null, confidence: base.confidence };
  return { value: list[0], confidence: base.confidence };
};

const genreList = (genresEntry) => {
  const base = passThrough(genresEntry);
  if (!base) return null;
  const list = Array.isArray(base.value) ? base.value.filter(Boolean) : [];
  return list.length > 0 ? base : null;
};

/**
 * A list-valued field (tags, keywords), cleaned to lowercase non-empty strings.
 * Returns null when the model gave nothing usable, so the caller can fall back.
 */
const stringList = (entry) => {
  const base = passThrough(entry);
  if (!base) return null;
  const list = Array.isArray(base.value)
    ? base.value
        .map((v) => (typeof v === "string" ? v.trim().toLowerCase() : ""))
        .filter(Boolean)
    : [];
  if (list.length === 0) return null;
  return { value: [...new Set(list)], confidence: base.confidence };
};

/** First non-null of several candidate entries. */
const firstOf = (...entries) => entries.find((e) => e && e.value != null) || null;

/**
 * @param {object} gemini  Parsed Gemini JSON output
 * @param {object} [context] Optional hints from the video object
 * @returns {object} Raw metadata payload
 */
export const geminiResponseToRawMetadata = (gemini = {}, context = {}) => {
  const runtimeMinutes = (() => {
    const seconds = Number(
      context.duration ?? context.video_duration ?? context.length
    );
    if (Number.isFinite(seconds) && seconds > 0) {
      return { value: Math.max(1, Math.round(seconds / 60)), confidence: 0.99 };
    }
    return null;
  })();

  const genres = genreList(gemini.genres);
  const genreNames = genres?.value ?? [];
  const tags = Array.isArray(genreNames) ? genreNames.map((g) => g.toLowerCase()) : [];

  return {
    basic: {
      title: passThrough(gemini.title),
      shortDescription: passThrough(gemini.tagline),
      longDescription: passThrough(gemini.description),
      genre: primaryGenre(gemini.genres),
      subgenre: passThrough(gemini.subgenre),
      language: passThrough(gemini.language),
      mood: passThrough(gemini.mood),
      ageRating: passThrough(gemini.ageRating),
      contentWarnings: passThrough(gemini.contentWarnings),
      year: passThrough(gemini.year),
    },
    // Discovery fields are now genuinely GENERATED where the model supplies them.
    // They used to be aliases — `keywords` and `seoDescription` were both just the
    // tagline, and `tags` was the genre list lowercased — which meant the CMS showed
    // populated-looking fields carrying no real information.
    //
    // Each still falls back to its old alias when the model omits the field, so an
    // older prompt (or a response missing a key) behaves exactly as before rather
    // than regressing to empty.
    discovery: {
      tags:
        stringList(gemini.tags) ||
        (genres ? { value: tags, confidence: genres.confidence } : null),
      keywords: stringList(gemini.keywords) || passThrough(gemini.tagline),
      searchMetadata: passThrough(gemini.title),
      seoTitle: passThrough(gemini.title),
      seoDescription:
        firstOf(passThrough(gemini.seoDescription), passThrough(gemini.tagline)),
    },
    media: {},
    trailer: null,
    audioTracks: null,
    technical: {},
    intelligence: {
      runtime: runtimeMinutes,
      episodeNumber: null,
      seasonNumber: null,
      contentType: null,
      categories: genres,
    },
    model: {
      name: GEMINI_MODEL,
      generatedAt: new Date().toISOString(),
    },
  };
};

export default geminiResponseToRawMetadata;
